import React, { useCallback, useEffect, useState } from 'react';
import automationService from '../../services/automationService';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import DataTable from '../../ui/DataTable/DataTable';
import { CellText, CellId, CellStatus, CellMuted } from '../../ui/DataTable/cells';
import { Field, Input } from '../../ui/Field/Field';

/**
 * Delete a customer from Ejar by identity number.
 *
 * Ejar has no screen for this and no list of individuals at all; its own audit log
 * records the engine's service account for every deletion, never the person who asked,
 * because Ejar has never heard of yelo's staff. So the log underneath is ours, and it is
 * the only place that says who.
 *
 * Nothing is deleted from a typed number alone. The identity is looked up first, the
 * name and the id are shown, and the engine checks the pair again before it removes
 * anything - a page left open while the number changed hands would otherwise delete a
 * stranger.
 */
const DeleteCustomerPanel = () => {
    const [identity, setIdentity] = useState('');
    const [found, setFound] = useState(null);
    const [looking, setLooking] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [notice, setNotice] = useState(null);
    const [log, setLog] = useState([]);
    const [logLoaded, setLogLoaded] = useState(false);

    const loadLog = useCallback(async () => {
        try {
            const r = await automationService.adminDeletions();
            setLog(r?.items || []);
        } catch (e) { /* the log is not worth an error banner */ }
        finally { setLogLoaded(true); }
    }, []);

    useEffect(() => { loadLog(); }, [loadLog]);

    const look = async () => {
        setLooking(true); setNotice(null); setFound(null); setConfirming(false);
        try {
            const r = await automationService.adminLookupCustomer(identity.trim());
            setFound(r);
            if (!r?.found) setNotice(r?.reason || 'Ejar does not know this identity');
        } catch (e) { setNotice(e.message); }
        finally { setLooking(false); }
    };

    const remove = async () => {
        setDeleting(true); setNotice(null);
        try {
            const r = await automationService.adminDeleteCustomer(
                found.customer_id, found.identity);
            setNotice(r.deleted
                ? `deleted ${r.name} (${r.customer_id})`
                : `not deleted — ${r.error || 'Ejar refused'}`);
            if (r.deleted) { setFound(null); setIdentity(''); }
            setConfirming(false);
            loadLog();
        } catch (e) { setNotice(e.message); }
        finally { setDeleting(false); }
    };

    const logColumns = [
        { key: 'at', header: 'When', cell: (r) => <span className="ds-cell-date whitespace-nowrap">{r.at}</span> },
        { key: 'by', header: 'Who', cell: (r) => <CellText>{r.by}</CellText> },
        { key: 'identity', header: 'Identity', cell: (r) => <CellId quickKind="identity">{r.identity}</CellId> },
        { key: 'name', header: 'Name', cell: (r) => <CellText>{r.name}</CellText> },
        {
            key: 'outcome',
            header: 'Outcome',
            cell: (r) => (
                <div className="flex flex-wrap items-center justify-center gap-2">
                    <CellStatus status={r.outcome} label={r.outcome} tone={r.outcome === 'deleted' ? 'danger' : 'neutral'} />
                    {r.had_contracts && (
                        <CellMuted>had contracts</CellMuted>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="mx-auto max-w-4xl space-y-6">
            <Card
                title="Delete a customer"
                subtitle="Permanent, and it cannot be undone. Ejar keeps no record of who asked, so every deletion here is written down below with your address."
            >
                {/* Found the hard way: a customer deleted here could not be entered
                    again, because Ejar had kept their documents and refused the numbers
                    as duplicates. Nothing in its API frees them. */}
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex gap-2">
                    <Icon glyph={ICONS.warning} size="sm" tone="warning" />
                    <div>
                        <b>Their documents stay behind.</b> Ejar keeps the identity and licence
                        numbers even after the customer is gone, and refuses them as duplicates
                        afterwards — so this person cannot be entered again with the same papers.
                        Delete only someone who should not exist at all.
                    </div>
                </div>

                <div className="mt-5 flex flex-wrap items-end gap-3">
                    <Field label="Identity number" className="grow">
                        <Input
                            className="font-mono"
                            value={identity}
                            onChange={(e) => { setIdentity(e.target.value); setFound(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') look(); }}
                            placeholder="784xxxxxxxxxxxx"
                        />
                    </Field>
                    <Button
                        variant="primary"
                        onClick={look}
                        disabled={!identity.trim() || looking}
                        isLoading={looking}
                        leadingIcon={<Icon glyph={ICONS.search} size="sm" />}
                    >
                        {looking ? 'looking…' : 'Find in Ejar'}
                    </Button>
                </div>

                {notice && (
                    <div className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-800" role="status" aria-live="polite">
                        {notice}
                    </div>
                )}

                {found?.found && (
                    <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <dl className="grid gap-3 text-sm md:grid-cols-2">
                            <div><dt className="text-gray-500">Name</dt>
                                <dd className="font-medium text-gray-900">{found.name || '—'}</dd></div>
                            <div><dt className="text-gray-500">Customer id</dt>
                                <dd className="font-mono font-medium text-gray-900">{found.customer_id}</dd></div>
                        </dl>

                        {/* A warning, not a wall: the decision is the owner's. */}
                        {found.has_contracts && (
                            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                This customer has contracts in Ejar. Deleting them removes
                                the person those contracts belong to.
                            </div>
                        )}

                        {!confirming ? (
                            <Button
                                variant="danger"
                                className="mt-4"
                                onClick={() => setConfirming(true)}
                                leadingIcon={<Icon glyph={ICONS.trash} size="sm" />}
                            >
                                Delete this customer
                            </Button>
                        ) : (
                            <div className="mt-4 flex flex-wrap items-center justify-end gap-3 rounded-lg border border-red-200 bg-red-50 p-3" role="alert">
                                <span className="text-sm font-medium text-red-800 me-auto">
                                    Delete {found.name}? This cannot be undone.
                                </span>
                                <Button variant="secondary" onClick={() => setConfirming(false)}>
                                    Keep
                                </Button>
                                <Button variant="danger" onClick={remove} disabled={deleting} isLoading={deleting}>
                                    {deleting ? 'deleting…' : 'Yes, delete'}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Card>

            <DataTable
                title="Who deleted what"
                columns={logColumns}
                data={log}
                rowKey={(r, i) => i}
                loading={!logLoaded}
                searchable={false}
                emptyTitle="Nothing yet."
                emptyIcon={ICONS.history}
            />
        </div>
    );
};

export default DeleteCustomerPanel;
